import { useQueries, useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { localeAtom } from 'src/stores/locale'
import { getBaseLanguageCode } from 'src/utils/tack'
import type { NormalizedTagItem } from '../apollo/tack'
import { orpcClient } from './client'
import { tackAutocompleteOptions } from './generated/@tanstack/react-query.gen'
import { tackGallery } from './generated/sdk.gen'
import { type BatchTackItem, loadTack } from './tackBatcher'

export type { BatchTackItem }

/**
 * Fetch tacks for the gallery scrollbar via oRPC.
 * Only for the home/tag page gallery display — not for search, autocomplete, or recommendations.
 */
export const fetchTackGallery = async (options: {
  first?: number
  language?: string
}): Promise<NormalizedTagItem[]> => {
  const { data } = await tackGallery({
    client: orpcClient,
    query: {
      first: options.first ?? undefined,
      lang: options.language
        ? getBaseLanguageCode(options.language)
        : undefined,
    },
    throwOnError: true,
  })
  return data.data.map(tack => ({
    id: tack.id,
    name: tack.displayName,
    displayName: tack.displayName,
    codeName: tack.codeName,
    thumbnailUrl: tack.thumbnailUrl ?? undefined,
    // Forwarded as a fallback: `thumbnailUrl` is nullable, so `TagGalleryItem`
    // falls back to `getImageThumbnailUrl(mediaId)` when the server returns
    // `thumbnailUrl: null`. Drop alongside the server-side legacy-field removal.
    mediaId: tack.mediaId ?? undefined,
    extra: {
      recommendType: tack.category === 'general' ? 'feature' : tack.category,
    },
    createdAt: tack.createdAt,
    updatedAt: tack.updatedAt,
  }))
}

/** ORPC tack autocomplete with CJK support (kana→romaji and default-list fallback handled server-side). */
export function useTackAutocomplete(
  keyword: string,
  opts?: { limit?: number; enabled?: boolean },
) {
  // Subscribe to `localeAtom` so the hook re-runs when the user switches
  // languages — `lang` flows into the react-query key, so a non-reactive
  // snapshot would keep returning stale `displayName` results.
  const locale = useAtomValue(localeAtom)
  const lang = getBaseLanguageCode(locale)
  const query = useQuery({
    ...tackAutocompleteOptions({
      client: orpcClient,
      query: {
        q: keyword.trim(),
        limit: opts?.limit ?? 20,
        lang,
      },
    }),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  })

  const tags: NormalizedTagItem[] =
    query.data?.data.map(tack => ({
      id: tack.id,
      name: tack.displayName,
      displayName: tack.displayName,
      codeName: tack.codeName,
      thumbnailUrl: tack.thumbnailUrl ?? undefined,
      // Forwarded as a fallback for the `thumbnailUrl: null` case; see
      // `fetchTackGallery` above.
      mediaId: tack.mediaId ?? undefined,
      extra: {
        // `recommendType` is a frontend styling alias for tag-button color variants;
        // renames Tack.category 'general' → 'feature' to match the color-variant name.
        recommendType: tack.category === 'general' ? 'feature' : tack.category,
      },
      createdAt: tack.createdAt,
      updatedAt: tack.updatedAt,
    })) ?? []

  return { tags, isLoading: query.isLoading }
}

/**
 * Hydrate full tack data for a list of ids. Each id is its own React Query
 * cache entry under `['tackItem', id, lang]`, so concurrent calls across
 * cards (and across feeds) automatically dedupe at the cache layer. The
 * actual network fetches are coalesced by `tackBatcher.loadTack` into one
 * /v2/tack/batch RPC per microtask × language.
 *
 * React Query is the single source of truth for freshness — `staleTime: 5min`
 * keeps each entry fresh during normal scroll, and any `invalidateQueries`
 * call on this key will trigger a real refetch (DataLoader has its own
 * cache disabled so it can't shadow invalidations). Server-omitted ids
 * resolve to `null` and stay cached for the same `staleTime` window; after
 * that they may be retried per RQ's normal remount/refocus behavior.
 */
export function useTacksByIds(
  tackIds: string[],
  lang: string,
  opts?: { enabled?: boolean },
): Map<string, BatchTackItem> {
  const enabled = opts?.enabled ?? true
  const queries = useQueries({
    queries: tackIds.map(id => ({
      queryKey: ['tackItem', id, lang] as const,
      queryFn: () => loadTack(id, lang),
      // Per-id cache survives scroll growth and re-renders; new ids hit the
      // batcher, already-cached ids skip the network entirely.
      staleTime: 5 * 60_000,
      enabled,
    })),
  })

  // Small per-card map (typically 1–3 entries). No memo: the Map allocation
  // is cheaper than serializing a stable dep key for useMemo.
  const map = new Map<string, BatchTackItem>()
  for (let i = 0; i < tackIds.length; i++) {
    const item = queries[i]?.data
    if (item) map.set(tackIds[i], item)
  }
  return map
}
